import React, { useState } from "react";
import { useTranslation } from "react-i18next"; 
import {
  Autocomplete,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { CloseRounded } from "@mui/icons-material";
import { Box } from "@mui/system";
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { connect } from "react-redux";
import * as actions from "../../api/actions/pos";
import { controller, endpoints } from "../../api/actions/api";
import { RmvEmptyProp, ToFormNestedObj } from "../../hooks/Method";
import { formatCurrency } from "../../hooks/Currency";

const Cart = (props) => {
  const { t } = useTranslation();
  const {
    cartItems,
    setCartItems,
    subTotal,
    setSubTotal,
    totalPay,
    setTotalPay,
    tax,
    setTax,
    selectedCustomer,
    setSelectedCustomer,
    _post,
    _getById,
    _store,
    _authUser,
    customersList,
    heldSales,
    handleHoldSale,
    handleResumeSale,
    handleDeleteHeldSale,
  } = props;

  const navigate = useNavigate();

  const location = useLocation();
  const isRefund = location.pathname.includes("/pos/refund");

  const [heldSalesOpen, setHeldSalesOpen] = useState(false);
  const [deleteHeldSale, setDeleteHeldSale] = useState(null);

  const [duplicateCustomer, setDuplicateCustomer] = useState(null);

  const [addCustomerOpen, setAddCustomerOpen] = useState(false);

  const [newCustomer, setNewCustomer] = useState({
    customerName: "",
    phone: "",
  });

  const [addingCustomer, setAddingCustomer] = useState(false);

  useEffect(() => {
    if (_store.id) {
      var _total = cartItems
        .map((j) => j.qty * j.price)
        .reduce((x, y) => x + y, 0);
      setSubTotal(_total.toFixed(2));

      var _taxItemTotal = cartItems
        .filter((j) => j.tax === 1)
        .map((j) => j.qty * j.price)
        .reduce((x, y) => x + y, 0);
      var _tax = (parseFloat(_store.tax) / 100) * _taxItemTotal;
      setTax(_tax.toFixed(2));

      setTotalPay((_tax + _total).toFixed(2));
    }

    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartItems, _store]);

  const rmvCartItem = (item) => {
    setCartItems((x) => [...x.filter((j) => j.productId !== item.productId)]);

    // Post CancelCartItem
    // Record cancelled cart items only during a NORMAL sale.
    // During Refund, removing an item only means:
    // "Do not refund this item."
    if (!isRefund && item.productId) {  
      const cancelItem = {
        cancelItem: RmvEmptyProp({
          ...item,
          productId: item.itemType === 2 ? "" : item.productId,
        }),
        userId: _authUser.id,
        storeId: _store.id,
      };
      _post(
        endpoints.Sell + controller.PostCancelCart,
        ToFormNestedObj(cancelItem),
        null
      );
    }
  };
   
  // increaseQty function to handle quantity increase for existing cart items

  const increaseQty = (item) => {
    setCartItems((items) =>
      items.map((j) =>
        j.productId === item.productId
          ? { ...j, qty: parseInt(j.qty) + 1 }
          : j
      )
    );
  };
  // Decrease Qty function to handle quantity decrease for existing cart items
  const decreaseQty = (item) => {
    if (item.qty <= 1) {
      rmvCartItem(item); // ✅ use your existing remove
    } else {
      setCartItems((items) =>
        items.map((j) =>
          j.productId === item.productId
            ? { ...j, qty: parseInt(j.qty) - 1 }
            : j
        )
      );
    }
  };

  const handleAddCustomer = () => {
    const customerName = newCustomer.customerName.trim();
    const phone = newCustomer.phone.trim();

    if (!customerName || !phone) {
      return;
    }

    setDuplicateCustomer(null);

    const formData = new FormData();

    formData.append("CustomerName", customerName);
    formData.append("Phone", phone);
    formData.append("StoreId", _store.id);

    if (_authUser?.id) {
      formData.append("UserId", _authUser.id);
    }

    setAddingCustomer(true);

    const onComplete = (res, err) => {
      setAddingCustomer(false);

      // ---------------------------------------
      // Successfully created
      // ---------------------------------------
      if (res?.status === 200) {
        const addedCustomer = res.result;

        setSelectedCustomer(addedCustomer);

        _getById(
          endpoints.Customer + controller.GetByStore,
          _store.id,
          actions.ActionType.SetCustomers
        );

        setNewCustomer({
          customerName: "",
          phone: "",
        });

        setDuplicateCustomer(null);
        setAddCustomerOpen(false);

        return;
      }

      // ---------------------------------------
      // Duplicate phone
      // ---------------------------------------
      if (err?.status === 400) {
        const existingCustomer = err.result;

        if (existingCustomer?.id) {
          setDuplicateCustomer(existingCustomer);
        }

        return;
      }
    };

    _post(
      endpoints.Customer,
      formData,
      null,
      onComplete
    );
  };

  return (
    <Paper className="shadow-xs br-8 p-15">

      {/* Customer Selection */}
      <Box className="mb-15">
        <Typography variant="subtitle2" className="mb-5">
          {t("customer")}
        </Typography>

        <Box
          sx={{
            display: "flex",
            gap: 1,
            alignItems: "center",
          }}
        >
          <Autocomplete
            size="small"
            sx={{ flex: 1 }}
            options={[
              {
                id: null,
                customerName: t("walkInCustomer"),
              },
              ...(customersList || []).filter(
                (customer) =>
                  Number(customer.storeId) === Number(_store?.id)
              ),
            ]}
            value={
              selectedCustomer || {
                id: null,
                customerName: t("walkInCustomer"),
              }
            }
            onChange={(event, newValue) => {
              setSelectedCustomer(
                newValue?.id ? newValue : null
              );
            }}
            getOptionLabel={(option) =>
              option?.customerName || t("walkInCustomer")
            }
            isOptionEqualToValue={(option, value) =>
              option?.id === value?.id
            }
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={t("searchCustomer")}
              />
            )}
          />

          <Button
            variant="contained"
            onClick={() => setAddCustomerOpen(true)}
            disabled={isRefund}
            sx={{
              minWidth: "45px",
              height: "40px",
              fontSize: "22px",
            }}
          >
            +
          </Button>
        </Box>
      </Box>
        
      {/* Details of products sold */}
      <TableContainer
        sx={{
          height: "calc(100vh - 296px)",
          overflow: "auto",
          paddingRight: "10px",
        }}
      >
        <Table size="small" aria-label="a dense table">
          <TableHead>
            <TableRow
              sx={{ "&:last-child td, &:last-child th": { padding: "0px" } }}
            >
              <TableCell></TableCell>
              <TableCell>{t("itemName")}</TableCell>
              <TableCell>{t("qty")}</TableCell>
              <TableCell>{t("price")}</TableCell>
              <TableCell>{t("subtotal")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {cartItems.map((item, i) => (
              <TableRow sx={{ "td, th": { padding: "5px 0px" } }} key={i}>

                {/* ✅ Remove button */}
                <TableCell>
                  <IconButton
                    onClick={() => rmvCartItem(item)}
                    size="small"
                    className="p-2 btn-error mr-10"
                  >
                    <CloseRounded className="fs-18" />
                  </IconButton>
                </TableCell>

                {/* ✅ Product name */}
                <TableCell className="fs-16" sx={{ minWidth: "150px" }}>
                  <Typography variant="subtitle2" className="pr-10">
                    {item.name || item.product?.name || ""}
                  </Typography>
                </TableCell>

                 {/* ✅ Qty with buttons */}
                {/*<TableCell sx={{ minWidth: "50px" }}>{item.qty}</TableCell>*/}
                <TableCell sx={{ minWidth: "120px" }}>
                  <Box display="flex" alignItems="center" gap={1}>

                    {/* ➖ Decrease */}
                    <IconButton
                      size="small"
                      onClick={() => decreaseQty(item)}
                    >
                      -
                    </IconButton>

                    {/* ✅ Quantity */}
                    <Typography>{item.qty}</Typography>

                    {/* ➕ Increase */}
                    <IconButton
                      size="small"
                      onClick={() => increaseQty(item)}
                    >
                      +
                    </IconButton>

                  </Box>
                </TableCell>

                {/* ✅ Price */}
                <TableCell className="fs-16" sx={{ minWidth: "80px" }}>
                  {formatCurrency(item.price, _store)}
                </TableCell>

                {/* ✅ Subtotal */}
                <TableCell sx={{ minWidth: "120px" }}>
                   {formatCurrency(item.qty * item.price, _store)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box className="mt-20 gray-color">
        <Box className="d-flex j-between">
          <Typography variant="h6">{t("subtotal")}:</Typography>
          <Typography variant="h6">{formatCurrency(subTotal, _store)}</Typography>
        </Box>
        <Box className="d-flex j-between mb-10">
          <Typography variant="h6">{t("tax")}({_store.tax}%):</Typography>
          <Typography variant="h6">{formatCurrency(tax, _store)}</Typography>
        </Box>

        <Box className="d-flex mb-10" sx={{ gap: 1 }}>
          <Button
            onClick={handleHoldSale}
            disabled={isRefund || cartItems.length === 0}
            variant="outlined"
            fullWidth
          >
            {t("holdSale")}
          </Button>

          <Button
            onClick={() => setHeldSalesOpen(true)}
            disabled={isRefund || heldSales.length === 0}
            variant="outlined"
            fullWidth
          >
            {t("heldSales")} ({heldSales.length})
          </Button>

        </Box>

        <Button
          onClick={() => navigate("/pos/checkout")}
          disabled={isRefund}
          className="btn-secondary d-flex j-between fs-20"
          sx={{ p: "12px" }}
          size="large"
          fullWidth
        >
          <span>{t("totalPay")}</span>
          <span>{formatCurrency(totalPay, _store)}</span>
        </Button>
      </Box>

      <Dialog
        open={heldSalesOpen}
        onClose={() => setHeldSalesOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {t("heldSales")} ({heldSales.length})
        </DialogTitle>

        <DialogContent dividers>
          {heldSales.map((sale) => {
            const itemCount = (sale.cartItems || []).reduce(
              (total, item) => total + Number(item.qty || 0),
              0
            );

            const saleTotal = (sale.cartItems || []).reduce(
              (total, item) =>
                total + Number(item.qty || 0) * Number(item.price || 0),
              0
            );

            return (
              <Box key={sale.id}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    py: 2,
                  }}
                >
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {sale.selectedCustomer?.customerName ||
                        t("walkInCustomer")}
                    </Typography>

                    <Typography variant="body2">
                      {itemCount} {t("items")} •{" "}
                      {formatCurrency(saleTotal, _store)}
                    </Typography>

                    <Typography variant="caption">
                      {new Date(sale.createdAt).toLocaleString()}
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      variant="contained"
                      onClick={() => {
                        handleResumeSale(sale);
                        setHeldSalesOpen(false);
                      }}
                    >
                      {t("resume")}
                    </Button>

                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => setDeleteHeldSale(sale)}
                    >
                      {t("delete")}
                    </Button>
                  </Box>

                </Box>

                <Divider />
              </Box>
            );
          })}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setHeldSalesOpen(false)}>
            {t("close")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deleteHeldSale)}
        onClose={() => setDeleteHeldSale(null)}
      >
        <DialogTitle>
          {t("deleteHeldSale")}
        </DialogTitle>

        <DialogContent>
          <Typography>
            {t("deleteHeldSaleConfirm")}
          </Typography>

          {deleteHeldSale && (
            <Typography sx={{ mt: 1 }} fontWeight="bold">
              {deleteHeldSale.selectedCustomer?.customerName ||
                t("walkInCustomer")}
            </Typography>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setDeleteHeldSale(null)}>
            {t("cancel")}
          </Button>

          <Button
            color="error"
            variant="contained"
            onClick={() => {
              handleDeleteHeldSale(deleteHeldSale.id);
              setDeleteHeldSale(null);
            }}
          >
            {t("delete")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Quick Add Customer */}
      <Dialog
        open={addCustomerOpen}
        onClose={() => {
          if (!addingCustomer) {
             setNewCustomer({
                customerName: "",
                phone: "",
              });

            setDuplicateCustomer(null);
            setAddCustomerOpen(false);
          }
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>
          {t("addCustomer")}
        </DialogTitle>

        <DialogContent dividers>
          <TextField
            autoFocus
            fullWidth
            required
            label={t("customerName")}
            value={newCustomer.customerName}
            onChange={(e) =>
              setNewCustomer((prev) => ({
                ...prev,
                customerName: e.target.value,
              }))
            }
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            required
            label={t("phone")}
            value={newCustomer.phone}
            onChange={(e) => {
              setNewCustomer((prev) => ({
                ...prev,
                phone: e.target.value,
              }))

              setDuplicateCustomer(null);
            }}
          />

          {duplicateCustomer && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                border: "1px solid",
                borderColor: "warning.main",
                borderRadius: 1,
              }}
            >
              <Typography
                variant="subtitle2"
                color="warning.main"
                fontWeight="bold"
              >
                {t("customerAlreadyExists")}
              </Typography>

              <Typography sx={{ mt: 1 }}>
                {t("customerName")}:{" "}
                <strong>{duplicateCustomer.customerName}</strong>
              </Typography>

              <Typography>
                {t("phone")}:{" "}
                <strong>{duplicateCustomer.phone}</strong>
              </Typography>

              <Button
                variant="contained"
                fullWidth
                sx={{ mt: 2 }}
                onClick={() => {
                  setSelectedCustomer(duplicateCustomer);

                  setNewCustomer({
                    customerName: "",
                    phone: "",
                  });

                  setDuplicateCustomer(null);
                  setAddCustomerOpen(false);
                }}
              >
                {t("selectCustomer")}
              </Button>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => {
              setNewCustomer({
                customerName: "",
                phone: "",
              });

              setDuplicateCustomer(null);
              setAddCustomerOpen(false);
            }}
            disabled={addingCustomer}
          >
            {t("cancel")}
          </Button>

          <Button
            variant="contained"
            onClick={handleAddCustomer}
            disabled={
              addingCustomer ||
              !newCustomer.customerName.trim() ||
              !newCustomer.phone.trim()
            }
          >
            {addingCustomer
              ? t("saving")
              : t("addCustomer")}
          </Button>
        </DialogActions>
      </Dialog>

    </Paper>
  );
};

const mapStateToProps = (state) => ({
  _store: state.pos.defStore,
  _authUser: state.pos.authUser,
  customersList: state.pos.customersList,
});

const mapActionToProps = {
  _post: actions.post,
  _getById: actions.getById,
};

export default connect(mapStateToProps, mapActionToProps)(Cart);
